import styled from 'styled-components'
import { theme } from '../../../theme/theme'
import { BodyLarge, Title3, Title4 } from '../../styles/Typography'
import { useWindowWidth } from '../../../hooks/useWindowWidth'
import { isTabletOrMobile } from '../../../utils/devices'
import { Arrow, Close, ErrorSign } from '../../icons'
import { useNavigate } from 'react-router-dom'
import { ComponentLayout } from '../../layouts/ComponentLayout'
import { OnClickButton } from '../../buttons/onclick/OnClickButton'
import { isIFAQ } from '@estercare/ester-shared'
import { useGetEntryById } from '../../../api/contentful/contentfulQueries'
import { Stack } from '@mui/material'
import { FAQ } from '../../Faq'

type Props = {
  pageTitle: string
  screeningTitle: string
  screeningDescription: string
  showConsentInfoText?: boolean
  screeningTitleChildren?: React.ReactNode
  screeningAdditionalChildren?: React.ReactNode
  fullWidthChildren?: React.ReactNode
  leftAlignText?: boolean
  errorMsg?: string
  hideButton?: boolean
  buttonText?: string
  onButtonClick?: () => void
  isButtonDisabled?: boolean
  isLoading?: boolean
  showFaq?: boolean
  closePath?: string
}

export const ScreeningViewTemplate = ({
  pageTitle,
  screeningTitle,
  screeningDescription,
  screeningTitleChildren,
  screeningAdditionalChildren,
  fullWidthChildren,
  leftAlignText = false,
  errorMsg,
  hideButton = false,
  buttonText,
  onButtonClick,
  isButtonDisabled,
  isLoading = false,
  showFaq = false,
  closePath = '/portal',
}: Props) => {
  const screeningFaq = useGetEntryById('5p3VrQzbeOSmNYQESSgUGd').data
  const width = useWindowWidth()
  const navigate = useNavigate()
  const iconSize = isTabletOrMobile(width) ? 24 : 32
  return (
    <>
      <HeaderContainer>
        <Section flexDirection="row" justifyContent="space-between">
          <Spacing />
          <Title4>{pageTitle}</Title4>
          <Close size={iconSize} onClick={() => navigate(closePath)} color={theme.color.plum} />
        </Section>
      </HeaderContainer>
      <ComponentLayout
        noMaxWidth
        paddingTop={isTabletOrMobile(width) ? theme.spacing.xsmall : theme.spacing.xxlarge}
        style={{
          height: fullWidthChildren ? 'auto' : '100%',
        }}
      >
        <Section $autoHeight={Boolean(fullWidthChildren)}>
          <ContentContainer>
            <TextContainer $leftAlign={leftAlignText}>
              <StyledTitle3>{screeningTitle}</StyledTitle3>
              <BodyLarge>{screeningDescription}</BodyLarge>
              {screeningTitleChildren}
            </TextContainer>
            <ConsentContainer>{screeningAdditionalChildren}</ConsentContainer>
            {errorMsg && (
              <ErrorContainer>
                <ErrorSign color={theme.color.red} size={28} />
                <BodyLarge>{errorMsg}</BodyLarge>
              </ErrorContainer>
            )}
          </ContentContainer>

          {!hideButton && buttonText && onButtonClick && (
            <ButtonContainer>
              <OnClickButton
                text={buttonText}
                onClick={onButtonClick}
                loading={isLoading}
                icon={<Arrow color={theme.color.white} size={15} />}
                disabled={isButtonDisabled}
              />
            </ButtonContainer>
          )}

          {showFaq && (
            <FaqContainer>{isIFAQ(screeningFaq) && <FAQ showImage={false} {...screeningFaq.fields} />}</FaqContainer>
          )}
        </Section>
        {fullWidthChildren && <FullWidthContainer>{fullWidthChildren}</FullWidthContainer>}
      </ComponentLayout>
    </>
  )
}

const HeaderContainer = styled.div`
  padding: ${theme.spacing.large}px ${theme.spacing.medium}px;

  & svg {
    cursor: pointer;
  }

  @media screen and (min-width: ${theme.breakpoint.medium}px) {
    border-bottom: 1px solid ${theme.color.beigeDark};
  }
`
const Section = styled(Stack)<{ $autoHeight?: boolean }>`
  max-width: 640px;
  margin: 0 auto;

  height: ${({ $autoHeight }) => ($autoHeight ? 'auto' : '100%')};
`
const ButtonContainer = styled.div`
  margin: 0 auto;
  & > button {
    width: 100%;
  }

  @media screen and (min-width: ${theme.breakpoint.medium}px) {
    padding-top: ${theme.spacing.medium}px;
    width: 50%;
  }
`
const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${theme.spacing.xlarge}px;
  padding-bottom: ${theme.spacing.medium}px;
`
const ConsentContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: start;
  text-align: center;
`
const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: ${theme.spacing.small}px;
  color: ${theme.color.red};
`
const StyledTitle3 = styled(Title3)`
  margin-bottom: ${theme.spacing.small}px;
`

const Spacing = styled.div``

const FaqContainer = styled.div`
  padding: ${theme.spacing.xxlarge}px 0;
`

const FullWidthContainer = styled.div`
  padding: ${theme.spacing.jumbo}px ${theme.spacing.medium}px ${theme.spacing.xxlarge}px;
  max-width: 800px;
  margin: 0 auto;
`

const TextContainer = styled.div<{ $leftAlign?: boolean }>`
  margin: ${({ $leftAlign }) => ($leftAlign ? '0' : '0 auto')};
  text-align: left;

  & p {
    white-space: pre-line;
  }
`
